import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Typography } from "@mui/material";
import AppButton from "../../../common_components/AppButton";
import CommonChip from "../../../common_components/CommonChip";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import IOSSwitch from "../../../common_components/IOSSwitch";
import { TextInput, NumberInput, Dropdown, FormRow, FormField } from "../../../common_components/FormInputs";
const DeleteIconSvg = (props) => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", style: { cursor: "pointer" }, ...props, children: _jsx("path", { d: "M2.5 5h1.667m0 0H17.5M4.167 5v11.667a1.667 1.667 0 0 0 1.666 1.667h8.334a1.667 1.667 0 0 0 1.666-1.667V5zm2.5 0V3.334a1.667 1.667 0 0 1 1.666-1.667h3.334a1.667 1.667 0 0 1 1.666 1.667V5m-5 4.167v5m3.334-5v5", stroke: "#535862", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const PermissionSwitchRow = ({ label, checked, onChange, }) => (_jsxs(Box, { sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        height: "44px",
        px: "4px",
    }, children: [_jsx(Typography, { className: "form-input-label", children: label }), _jsx(IOSSwitch, { checked: checked, onChange: (e) => onChange(e.target.checked), size: "small" })] }));
const ActionsChipInput = ({ actions, onAdd, onRemove, }) => {
    const [inputVal, setInputVal] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const handleAdd = () => {
        const trimmed = inputVal.trim();
        if (trimmed && !actions.includes(trimmed)) {
            onAdd(trimmed);
            setInputVal("");
            setIsAdding(false);
        }
    };
    return (_jsxs(Box, { children: [_jsx(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    border: "1px solid #D0D5DD",
                    borderRadius: "8px",
                    px: "12px",
                    minHeight: "44px",
                    py: "4px",
                    background: "#fff",
                    gap: 1,
                }, children: actions.map((action, idx) => (_jsxs(Box, { sx: { position: "relative", display: "inline-flex", alignItems: "center" }, children: [_jsx(CommonChip, { label: action, renderType: "tag", index: idx, sx: { paddingRight: "18px" } }), _jsx(IconButton, { size: "small", onClick: () => onRemove(idx), sx: {
                                position: "absolute",
                                right: "1px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                p: "2px",
                                color: "#667085",
                                "&:hover": { background: "transparent", color: "#344054" },
                            }, children: _jsx(CloseIcon, { sx: { fontSize: 12 } }) })] }, idx))) }), !isAdding && (_jsx(Box, { sx: { mt: 1, display: "flex", justifyContent: "flex-end" }, children: _jsx(AppButton, { variant: "outlined", onClick: () => setIsAdding(true), startIcon: _jsx(AddIcon, { sx: { fontSize: 14 } }), sx: {
                        borderColor: "#D0D5DD",
                        color: "#344054",
                        textTransform: "none",
                        "&:hover": { borderColor: "#98A2B3", backgroundColor: "#F9FAFB" },
                    }, children: "Add actions" }) })), isAdding && (_jsxs(Box, { sx: { mt: 1, display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(TextInput, { autoFocus: true, placeholder: "e.g. AM Performance", value: inputVal, onChange: (e) => setInputVal(e.target.value), onKeyDown: (e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleAdd();
                            }
                            if (e.key === "Escape") {
                                setIsAdding(false);
                                setInputVal("");
                            }
                        } }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, ml: "auto" }, children: [_jsx(AppButton, { variant: "outlined", onClick: () => {
                                    setIsAdding(false);
                                    setInputVal("");
                                }, sx: {
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    borderColor: "#D0D5DD",
                                    color: "#344054",
                                }, children: "Cancel" }), _jsx(AppButton, { variant: "contained", onClick: handleAdd, disabled: !inputVal.trim(), sx: { textTransform: "none", whiteSpace: "nowrap" }, children: "Add Action" })] })] }))] }));
};
// Helpers
const createEmptyModule = () => ({
    _id: "",
    module_name: "",
    key: "",
    url: "",
    order: 0,
    permissions: {
        access: true,
        actions: [],
        client_scope: "assigned",
        refresh: false,
    },
    sub_modules: [],
});
// Component
const AddEditModuleDrawer = ({ open, onClose, mode, moduleData, parentModule, allModules = [], onSave }) => {
    const [moduleTree, setModuleTree] = useState(createEmptyModule());
    React.useEffect(() => {
        if (mode === "edit" && moduleData) {
            setModuleTree({ ...moduleData, sub_modules: moduleData.sub_modules || [] });
        }
        if (mode === "create") {
            setModuleTree(createEmptyModule());
        }
    }, [mode, moduleData]);
    const updateAtPath = (path, updater) => {
        setModuleTree(prev => {
            const clone = structuredClone ? structuredClone(prev) : JSON.parse(JSON.stringify(prev));
            let node = clone;
            path.forEach(i => { node = node.sub_modules[i]; });
            updater(node);
            return clone;
        });
    };
    const addSubModule = (path) => {
        updateAtPath(path, node => { node.sub_modules.push(createEmptyModule()); });
    };
    const removeSubModule = (path) => {
        setModuleTree(prev => {
            const clone = structuredClone ? structuredClone(prev) : JSON.parse(JSON.stringify(prev));
            let parent = clone;
            path.slice(0, -1).forEach(i => { parent = parent.sub_modules[i]; });
            parent.sub_modules.splice(path[path.length - 1], 1);
            return clone;
        });
    };
    const handleSave = () => {
        onSave({ ...moduleTree, parent_id: parentModule?._id || null });
        onClose();
    };
    const findSiblingCountForModule = React.useCallback((modules, target) => {
        const targetAny = target;
        const targetId = targetAny?._id || targetAny?.id;
        const isSameModule = (candidate) => {
            const candidateAny = candidate;
            const candidateId = candidateAny?._id || candidateAny?.id;
            if (targetId && candidateId) {
                return String(targetId) === String(candidateId);
            }
            const sameKey = !!target.key && !!candidate.key && target.key === candidate.key;
            const sameUrl = !!target.url && !!candidate.url && target.url === candidate.url;
            const sameName = !!target.module_name && !!candidate.module_name && target.module_name === candidate.module_name;
            return sameKey || (sameName && sameUrl);
        };
        for (const current of modules || []) {
            if (isSameModule(current)) {
                const siblingCount = modules.length;
                return siblingCount > 0 ? siblingCount : 1;
            }
            const nested = findSiblingCountForModule(current.sub_modules || [], target);
            if (nested !== null) {
                return nested;
            }
        }
        return null;
    }, []);
    const getSiblingCount = (path) => {
        // Root module order is based on modules under selected parent.
        if (path.length === 0) {
            if (mode === "edit" && moduleData && allModules.length > 0) {
                const siblingCount = findSiblingCountForModule(allModules, moduleData);
                if (siblingCount !== null)
                    return siblingCount;
            }
            const rootCount = parentModule?.sub_modules?.length ?? 0;
            if (rootCount > 0)
                return rootCount;
            return 1;
        }
        // Nested module order is based on its parent's direct sub_modules count.
        let parent = moduleTree;
        path.slice(0, -1).forEach((idx) => {
            parent = parent.sub_modules?.[idx];
        });
        const nestedCount = parent?.sub_modules?.length ?? 0;
        return nestedCount > 0 ? nestedCount : 1;
    };
    const renderModule = (module, path = []) => {
        const permissions = module.permissions || {
            access: true,
            refresh: false,
            client_scope: "assigned",
            actions: [],
        };
        return (_jsxs("div", { style: {
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 16,
                margin: 6,
                marginBottom: 16,
                background: "#fff",
            }, children: [_jsxs(FormRow, { columns: 2, gap: 16, children: [_jsx(FormField, { children: _jsx(TextInput, { label: "Module Name", placeholder: "e.g. Active Clients", value: module.module_name, onChange: (e) => updateAtPath(path, n => { n.module_name = e.target.value; }) }) }), _jsx(FormField, { children: _jsx(TextInput, { label: "Key", placeholder: "e.g. active_clients", value: module.key, onChange: (e) => updateAtPath(path, n => { n.key = e.target.value; }) }) })] }), _jsxs(FormRow, { columns: 2, gap: 16, children: [_jsx(FormField, { children: _jsx(TextInput, { label: "URL", placeholder: "e.g. active-clients", value: module.url, onChange: (e) => updateAtPath(path, n => { n.url = e.target.value; }) }) }), _jsx(FormField, { children: mode === "edit" ? (_jsx(Dropdown, { label: "Order", value: module.order ? String(module.order) : "", placeholder: "Select order", options: Array.from({
                                    length: Math.max(getSiblingCount(path), Number(module.order) || 0, 1),
                                }, (_, i) => ({ label: String(i + 1), value: String(i + 1) })), onChange: (value) => updateAtPath(path, n => {
                                    n.order = Number(value);
                                }) })) : (_jsx(NumberInput, { label: "Order", placeholder: "e.g. 1", value: module.order, onChange: (e) => updateAtPath(path, n => { n.order = Number(e.target.value); }) })) })] }), _jsxs(Box, { sx: { mb: 2 }, children: [_jsxs(FormRow, { columns: 3, gap: 16, children: [_jsx(PermissionSwitchRow, { label: "Access", checked: !!permissions.access, onChange: (val) => updateAtPath(path, n => { n.permissions.access = val; }) }), _jsx(PermissionSwitchRow, { label: "Refresh", checked: !!permissions.refresh, onChange: (val) => updateAtPath(path, n => { n.permissions.refresh = val; }) }), _jsx(PermissionSwitchRow, { label: "Client Scope", checked: permissions.client_scope === "all", onChange: (val) => updateAtPath(path, n => {
                                        n.permissions.client_scope = val ? "all" : "assigned";
                                    }) })] }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsxs(FormField, { children: [_jsx(Typography, { className: "form-input-label", children: "Actions" }), _jsx(ActionsChipInput, { actions: permissions.actions || [], onAdd: (val) => updateAtPath(path, n => {
                                            if (!n.permissions.actions)
                                                n.permissions.actions = [];
                                            n.permissions.actions.push(val);
                                        }), onRemove: (idx) => updateAtPath(path, n => {
                                            n.permissions.actions.splice(idx, 1);
                                        }) })] }) })] }), (module.sub_modules || []).map((child, index) => renderModule(child, [...path, index])), _jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mt: 1.5 }, children: [_jsx(AppButton, { variant: "outlined", onClick: () => addSubModule(path), startIcon: _jsx(AddIcon, { sx: { fontSize: 14 } }), sx: {
                                border: "1px solid #D0D5DD",
                                borderRadius: "8px",
                                color: "#344054",
                                textTransform: "none",
                                "&:hover": { borderColor: "#98A2B3", backgroundColor: "#F9FAFB" },
                            }, children: "Add sub module" }), path.length > 0 && (_jsx(IconButton, { sx: {
                                border: "1px solid #D0D5DD",
                                borderRadius: "8px",
                                p: "7px",
                                color: "#344054",
                                "&:hover": { background: "#f5f5f5" },
                            }, children: _jsx(DeleteIconSvg, { onClick: () => removeSubModule(path) }) }))] })] }, path.join("-") || "root"));
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: mode === "edit" ? "Edit Module" : "Add Module", headerActions: _jsx(AppButton, { variant: "contained", startIcon: _jsx(SaveIcon, {}), onClick: handleSave, children: "Save" }), width: "md", children: _jsx("div", { className: "inner-body", children: renderModule(moduleTree) }) }));
};
export default AddEditModuleDrawer;
